import React, { useEffect, useRef } from 'react';
import '../Pages/styles.css'; // Import the prefixed CSS - Assuming this path is correct relative to the component file

// --- Import FontAwesomeIcon Component ---
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// --- Import ALL Necessary Icons from Font Awesome (Updated for Inventory Management) ---
import {
    // Core Concepts & General
    faBoxesStacked,     // Inventory / Stock
    faWarehouse,        // Storage / Facility
    faClipboardList,    // Management / Tracking / Audit
    faTasks,            // Efficiency / Operations / Services
    // Why Essential (Benefits)
    faDollarSign,       // Cost Control / Budgeting / Cost Opt.
    faHeartPulse,       // Improved Patient Care
    faBolt,             // Operational Efficiency
    faCheckCircle,      // Regulatory Compliance / Quality
    faDatabase,         // Data-Driven Decision-Making
    // Systematic Approach (AAA)
    faSearch,           // Audit / Review
    faBalanceScale,     // Assessment / Evaluation
    faChartBar,         // Analysis / Optimization
    // Key Services
    faCalendarAlt,      // Planning & Forecasting
    faShoppingCart,     // Procurement Management
    faLaptopCode,       // Inventory Software Integration
    faCalculator,       // Cost Optimization / Budgeting (reuse faDollarSign if preferred)
    faChalkboardTeacher,// Training & Support
    // Keep other potentially useful icons if needed
    // Icons likely unused from previous versions:
    /*
    faMicroscope, faTools, faLightbulb, faCog, faHandshake, faLaptopCode, faWeightHanging,
    faSitemap, faWrench, faNetworkWired, faComments, faUserTie, faLifeRing, faUserCheck,
    faGlobe, faLeaf, faExpandArrowsAlt, faBiohazard, faAward, faProjectDiagram, faPalette,
    faDirections, faRulerCombined, faBuilding
    */
} from '@fortawesome/free-solid-svg-icons'; // Using free solid icons


// --- Intersection Observer Hooks (No changes needed in hook logic) ---
const useIntersectionObserverTitle = (options) => {
    const containerRef = useRef(null);
    const [isVisible, setIsVisible] = React.useState(false);
    const callbackFunction = (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) setIsVisible(true);
    };
    useEffect(() => {
        const observer = new IntersectionObserver(callbackFunction, options);
        const currentRef = containerRef.current;
        if (currentRef) observer.observe(currentRef);
        return () => { if (currentRef) observer.unobserve(currentRef); };
    }, [containerRef, options]);
    return [containerRef, isVisible];
};

const useIntersectionObserverElements = (options) => {
    useEffect(() => {
        const observerCallback = (entries, observer) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    const target = entry.target;
                    const parent = target.parentElement;
                    const siblings = parent ? Array.from(parent.children).filter(el => el.classList.contains('cliniqor-fade-in-up')) : [target];
                    const index = siblings.indexOf(target);
                    const staggerIndex = index >= 0 ? index : 0;

                    target.style.setProperty('--item-index', staggerIndex % 8); // Keep stagger logic
                    target.classList.add('cliniqor-visible'); // Use prefixed class
                    observer.unobserve(target);
                }
            });
        };
        const observer = new IntersectionObserver(observerCallback, options);
        const targets = document.querySelectorAll('.cliniqor-fade-in-up');
        targets.forEach(target => observer.observe(target));
        return () => targets.forEach(target => { if (target) observer.unobserve(target); });
    }, [options]);
};
// --- End Observer Hooks ---


function App() {

    // --- Observer Setup for Titles (Adjusted Refs) ---
    const titleObserverOptions = { threshold: 0.2 };
    const [title1Ref, title1Visible] = useIntersectionObserverTitle(titleObserverOptions); // Why Essential?
    const [title2Ref, title2Visible] = useIntersectionObserverTitle(titleObserverOptions); // Systematic Approach (AAA)
    const [title3Ref, title3Visible] = useIntersectionObserverTitle(titleObserverOptions); // Key Services

    // --- Observer Setup for General Fade-In Elements ---
    useIntersectionObserverElements({ threshold: 0.1 });

    // Choose a relevant hero background image for inventory/logistics
    const heroBgImage = 'https://images.unsplash.com/photo-1586473219010-2ffc57b0d282?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1920&q=80'; // Image of warehouse/boxes

  return(
    // Use prefixed base wrapper class
    <div className="cliniqor-app-wrapper">

      {/* --- HERO SECTION --- */}
      <header
        className="cliniqor-hero-section" // Prefixed
        style={{ backgroundImage: `url(${heroBgImage})` }}
      >
        <div className="cliniqor-container"> {/* Prefixed */}
          <div className="cliniqor-hero-content"> {/* Prefixed */}
            {/* --- NEW HERO CONTENT --- */}
            <h1>Comprehensive Hospital Inventory Management</h1>
            <p className="cliniqor-sub-heading">
              Maximizing Efficiency and Reducing Operational Costs with Cliniqor
            </p>
            <p className="cliniqor-intro-text">
              Ensuring continuous availability of medical supplies, pharmaceuticals, and equipment without overstocking. Cliniqor provides strategic consulting for seamless, cost-effective, and efficient inventory systems.
            </p>
            {/* --- END NEW HERO CONTENT --- */}
          </div>
        </div>
      </header>

      <main>
        {/* --- INTRO SECTION (Core & Compliance) --- */}
        <section className="cliniqor-section cliniqor-section-first cliniqor-section-bg-gradient cliniqor-intro-section">
          <div className="cliniqor-container">
             <div className="cliniqor-intro-grid">
                 <div className="cliniqor-intro-text-content cliniqor-fade-in-up">
                     {/* --- NEW INTRO CONTENT --- */}
                     <h3>The Core of Effective Hospital Inventory Management</h3>
                     <p>Maintaining an optimal balance between supply and demand is crucial. Inefficient systems lead to stockouts (delaying treatment) or excess inventory (tying up resources), increasing costs and compromising care.</p>
                     <p>Regulatory compliance is also key. Adherence to standards for supply management, patient safety, equipment certification, and drug control is mandatory. Cliniqor ensures your system complies, offering support for audits and documentation.</p>
                     {/* --- END NEW INTRO CONTENT --- */}
                 </div>
             </div>
          </div>
        </section>

        {/* --- WHY ESSENTIAL? (Features Section - 5 items) --- */}
        <section className="cliniqor-section cliniqor-features-section cliniqor-section-bg-light">
          <div className="cliniqor-container">
            <div ref={title1Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title1Visible ? 'cliniqor-visible' : ''}`}>
              {/* --- NEW SECTION TITLE --- */}
              <h2>Why Is Inventory Management Essential for Healthcare?</h2>
              <p>Impacting efficiency, sustainability, and patient well-being.</p>
              {/* --- END NEW SECTION TITLE --- */}
            </div>
            <div className="cliniqor-features-grid"> {/* Grid structure maintained (5 items) */}
              {/* --- NEW FEATURE CARDS --- */}
              <div className="cliniqor-feature-card cliniqor-fade-in-up">
                <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faDollarSign} /></div>
                    <h4>1. Cost Control</h4>
                    <p>Eliminating costs from overstocking, expired products, and underutilization for prudent financial management.</p>
                </div>
              </div>
              <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faHeartPulse} /></div>
                    <h4>2. Improved Patient Care</h4>
                    <p>Ensuring timely, uninterrupted treatment by minimizing delays due to unavailable supplies/equipment.</p>
                </div>
              </div>
              <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faBolt} /></div>
                    <h4>3. Operational Efficiency</h4>
                    <p>Streamlining workflows, reducing delays, optimizing space, and allowing staff focus on care.</p>
                </div>
              </div>
               <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faCheckCircle} /></div>
                    <h4>4. Regulatory Compliance</h4>
                    <p>Meeting requirements for drug safety, patient confidentiality, and equipment standards, avoiding fines.</p>
                </div>
              </div>
               <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faDatabase} /></div>
                    <h4>5. Data-Driven Decision-Making</h4>
                    <p>Providing valuable data to guide stock adjustments and procurement strategies for flexibility.</p>
                </div>
              </div>
              {/* --- END NEW FEATURE CARDS --- */}
            </div>
          </div>
        </section>

        {/* --- SYSTEMATIC APPROACH (AAA) (Detailed Planning Section - 3 items) --- */}
        <section className="cliniqor-section cliniqor-section-bg-texture cliniqor-detailed-planning-section"> {/* Kept existing classes */}
          <div className="cliniqor-container">
             <div ref={title2Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title2Visible ? 'cliniqor-visible' : ''}`}>
              {/* --- NEW SECTION TITLE --- */}
              <h2>A Systematic Approach to Hospital Inventory Management (Audit, Assessment, Analysis)</h2>
               <p>A structured process ensuring no aspect is overlooked, identifying inefficiencies and implementing strategies.</p>
               {/* --- END NEW SECTION TITLE --- */}
            </div>
            {/* Using Planning Items for the 3 Stages */}
            <div className="cliniqor-planning-items-wrapper">
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faSearch} /></div>
                    <div className="cliniqor-content"><h4>1. Audit: Comprehensive Review</h4> <p>Detailed inventory count, storage evaluation, turnover rate review, procurement/supply chain analysis.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faBalanceScale} /></div>
                    <div className="cliniqor-content"><h4>2. Assessment: Identifying Inefficiencies</h4> <p>Pinpointing blocked funds in excess stock, reviewing departmental performance, analyzing vendor value, evaluating turnover.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faChartBar} /></div>
                    <div className="cliniqor-content"><h4>3. Analysis: Implementing Solutions</h4> <p>Identifying excess stock reduction, recommending process improvements, suggesting tech (RFID), enhancing vendor relations.</p> </div>
                 </div>
            </div>
          </div>
        </section>

        {/* --- KEY SERVICES (Reusing Features Layout - 5 items) --- */}
        <section className="cliniqor-section cliniqor-features-section cliniqor-section-bg-light">
           <div className="cliniqor-container">
              <div ref={title3Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title3Visible ? 'cliniqor-visible' : ''}`}>
                 <h2>Key Services in Hospital Inventory Management</h2>
                 <p>Optimizing your inventory system for maximum efficiency and cost-effectiveness.</p>
              </div>
              <div className="cliniqor-features-grid">
                    {/* --- Cards for Key Services --- */}
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faCalendarAlt} /></div>
                            <h4>1. Inventory Planning & Forecasting</h4>
                            <p>Custom strategies, demand forecasting, and maintaining optimal stock levels.</p>
                        </div>
                    </div>
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faShoppingCart} /></div>
                            <h4>2. Procurement Management</h4>
                            <p>Streamlining purchasing, ensuring supplier quality, and negotiating best terms.</p>
                        </div>
                    </div>
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faLaptopCode} /></div>
                            <h4>3. Inventory Software Integration</h4>
                            <p>Automating tracking, ordering, and reporting to minimize error and improve accuracy.</p>
                        </div>
                    </div>
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faCalculator} /></div>
                            <h4>4. Cost Optimization & Budgeting</h4>
                            <p>Reducing costs via better allocation, turnover, waste reduction, and detailed budgeting.</p>
                        </div>
                    </div>
                     <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faChalkboardTeacher} /></div>
                            <h4>5. Training and Support</h4>
                            <p>Equipping staff with best practices for long-term success in inventory management.</p>
                        </div>
                    </div>
               </div>
           </div>
        </section>

       {/* --- CONCLUSION SECTION (Simple Text Section) --- */}
        <section className="cliniqor-section"> {/* Default background */}
           <div className="cliniqor-container">
              <div className="cliniqor-section-title cliniqor-fade-in-up"> {/* No specific ref needed */}
                 <h2>Enhancing Hospital Performance Through Effective Inventory Management</h2>
              </div>
              <div className="cliniqor-text-content cliniqor-fade-in-up">
                   <p>Effective inventory management is essential for reducing costs, improving patient care, and ensuring operational efficiency. Partnering with Cliniqor allows hospitals to streamline processes, eliminate inefficiencies, and create sustainable systems optimizing resource use.</p>
                   <p>Our comprehensive approach equips your hospital to handle inventory needs effectively, enabling more focused patient care. Let Cliniqor help build a robust strategy aligned with your goals for maximum efficiency, cost-effectiveness, and patient satisfaction, achieving operational excellence.</p>
              </div>
           </div>
        </section>


      </main>

      {/* --- FOOTER CTA --- */}
      <footer className="cliniqor-section cliniqor-section-narrow cliniqor-footer-cta">
        <div className="cliniqor-container cliniqor-fade-in-up">
          {/* --- UPDATED FOOTER CONTENT --- */}
          <h3>Optimize Your Hospital's Inventory Management</h3>
          <p>Transform your hospital’s inventory system with Cliniqor's expert consulting. Contact us today to achieve operational excellence, reduce costs, and enhance patient care.</p>
          <a href="#contact" className="cliniqor-cta-button">Request Inventory Consultation</a>
          {/* --- END UPDATED FOOTER CONTENT --- */}
        </div>
      </footer>
    </div> // End cliniqor-app-wrapper
  );
}

export default App;